import { useEffect } from "react";
import { Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, TextField, Typography } from "@mui/material";

interface AnularFacturaDialogProps {
  open: boolean;
  loading: boolean;
  numero?: string;
  // Un Borrador no tiene efecto fiscal: queda anulado sin generar factura de anulación.
  esBorrador: boolean;
  // Rectificativa en Borrador: su original queda bloqueada mientras no se elimine.
  esRectificativa?: boolean;
  motivo: string;
  onMotivoChange: (motivo: string) => void;
  confirmar: boolean;
  onConfirmarChange: (confirmar: boolean) => void;
  onClose: () => void;
  onConfirm: () => void;
}

// Diálogo de confirmación para anular una factura, compartido por los detalles de cita, parcial, clase y manual.
const AnularFacturaDialog = ({
  open, loading, numero, esBorrador, esRectificativa, motivo, onMotivoChange, confirmar, onConfirmarChange, onClose, onConfirm,
}: AnularFacturaDialogProps) => {
  // El motivo y la confirmación viven en el diálogo que lo abre: se limpian al abrir y al cerrar por cualquier vía
  // (cancelar, Esc, clic fuera o acción terminada), para que no quede nada de una anulación anterior.
  useEffect(() => {
    onMotivoChange("");
    onConfirmarChange(false);
    return () => {
      onMotivoChange("");
      onConfirmarChange(false);
    };
  }, []);

  return (
  <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
    <DialogTitle sx={{ pb: 0.5 }}>
      <Typography component="span" sx={{ fontSize: "1.3rem", fontWeight: "bold", display: "block" }}>
        Anular factura
      </Typography>
      {numero && (
        <Typography component="span" variant="body2" color="text.secondary">
          Factura nº {numero}
        </Typography>
      )}
    </DialogTitle>

    <DialogContent sx={{ pt: 2 }}>
      <Alert severity="warning" sx={{ mb: 3 }}>
        {esBorrador
          ? esRectificativa
            ? "Se anulará esta rectificativa. La factura original quedará bloqueada hasta que un administrador la elimine"
            : "Se anulará el borrador sin factura de anulación ni número. Un administrador podrá eliminarlo después"
          : "Acción irreversible: se emitirá una factura de anulación (importes en negativo, con el siguiente número) y esta factura quedará anulada"}
      </Alert>

      <TextField
        autoFocus
        fullWidth
        multiline
        minRows={3}
        required={!esBorrador}
        label={esBorrador ? "Motivo de la anulación (opcional)" : "Motivo de la anulación"}
        helperText="Quedará registrado en la factura."
        value={motivo}
        onChange={(e) => onMotivoChange(e.target.value)}
        disabled={loading}
      />

      <FormControlLabel
        sx={{ mt: 2 }}
        control={
          <Checkbox
            checked={confirmar}
            onChange={(e) => onConfirmarChange(e.target.checked)}
            disabled={loading}
          />
        }
        label="Confirmo que quiero anular esta factura"
      />
    </DialogContent>

    <DialogActions sx={{ px: 3, py: 2 }}>
      <Button onClick={onClose} disabled={loading}>
        Cancelar
      </Button>
      <Button
        variant="contained"
        color="error"
        disabled={loading || (!esBorrador && !motivo.trim()) || !confirmar}
        onClick={onConfirm}
      >
        {loading ? "Anulando..." : "Anular factura"}
      </Button>
    </DialogActions>
  </Dialog>
  );
};

export default AnularFacturaDialog;
